import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { 
  TrendingUp, Clock, CheckCircle, Target, Trophy, XCircle, BarChart3, AlertTriangle, RefreshCw, Shield 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('all');
  
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/signals/performance`);
        console.log('Performance API Response:', response.data);
        console.log('avg_rr from API:', response.data?.avg_rr);
        setStats(response.data);
      } catch (err) {
        console.error('Performance fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPerformance();
  }, []);
  
  if (loading) {
    return (
      <div className="text-center py-16">
        <RefreshCw className="h-8 w-8 text-brand-green animate-spin mx-auto mb-4" />
        <p className="text-slate-400">Performans verileri yükleniyor...</p>
      </div>
    );
  }
  
  const launchDate = stats?.platform_launch ? new Date(stats.platform_launch).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '1 Ocak 2026';
  const signals = stats?.signals || [];
  
  // Only show completed signals (has TP hit or SL hit)
  const completedSignals = signals.filter(s => 
    s.tp1_hit || s.tp2_hit || s.tp3_hit || s.sl_hit || s.outcome_status === 'stopped'
  );
  
  const symbols = [...new Set(completedSignals.map(s => s.symbol))];
  
  // Apply additional filters on completed signals
  const filteredSignals = completedSignals.filter(s => {
    if (filter === 'tp1' && !s.tp1_hit) return false;
    if (filter === 'tp2' && !s.tp2_hit) return false;
    if (filter === 'tp3' && !s.tp3_hit) return false;
    if (filter === 'stopped' && !s.sl_hit && s.outcome_status !== 'stopped') return false;
    if (symbolFilter !== 'all' && s.symbol !== symbolFilter) return false;
    return true;
  });
  
  const getOutcomeLabel = (signal) => {
    if (signal.tp3_hit) return { label: 'TP3 ✓', color: 'text-emerald-400', bg: 'bg-emerald-500/20' };
    if (signal.tp2_hit) return { label: 'TP2 ✓', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (signal.tp1_hit) return { label: 'TP1 ✓', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    if (signal.sl_hit || signal.outcome_status === 'stopped') return { label: 'STOP', color: 'text-slate-400', bg: 'bg-slate-500/20' };
    return { label: '-', color: 'text-slate-400', bg: 'bg-slate-500/20' };
  };
  
  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-purple-400" />
          Geçmiş Sinyaller
        </h2>
      </div>
      
      {/* Performans Özeti */}
      <Card className="border-slate-700/50 bg-slate-900/50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Performans Özeti</h3>
            <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
              Tamamlanmış: {completedSignals.length} Sinyal
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-5 bg-green-900/20 rounded-lg border border-green-500/20">
              <div className="flex items-center justify-center gap-1 mb-1">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-xs text-green-400">TP1</span>
              </div>
              <p className="text-3xl font-bold text-green-400 mb-1">%{stats?.tp1_rate || 0}</p>
              <p className="text-xs text-slate-300">En az 1 hedefe ulaştı</p>
            </div>
            
            <div className="text-center p-5 bg-amber-900/20 rounded-lg border border-amber-500/20">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Target className="h-4 w-4 text-amber-400" />
                <span className="text-xs text-amber-400">TP2</span>
              </div>
              <p className="text-3xl font-bold text-amber-400 mb-1">%{stats?.tp2_rate || 0}</p>
              <p className="text-xs text-slate-300">Ana hedefe ulaştı</p>
            </div>
            
            <div className="text-center p-5 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Trophy className="h-4 w-4 text-emerald-400" />
                <span className="text-xs text-emerald-400">TP3</span>
              </div>
              <p className="text-3xl font-bold text-emerald-400 mb-1">%{stats?.tp3_rate || 0}</p>
              <p className="text-xs text-slate-300">Tam hedefe ulaştı</p>
            </div>
            
            <div className="text-center p-5 bg-purple-900/20 rounded-lg border border-purple-500/20">
              <div className="flex items-center justify-center gap-1 mb-1">
                <BarChart3 className="h-4 w-4 text-purple-400" />
                <span className="text-xs text-purple-400">Risk/Kazanç</span>
              </div>
              <p className="text-3xl font-bold text-purple-400 mb-1">
                {stats?.avg_rr && stats.avg_rr > 0 ? `1:${stats.avg_rr.toFixed(1)}` : '-'}
              </p>
              <p className="text-xs text-slate-300">Ortalama</p>
            </div>
          </div>
          
          {/* Hesaplama Notu */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 mt-4">
            <p className="text-sm text-slate-300 text-center">
              💡 <strong className="text-white">TP (Take Profit)</strong> = Kar alma hedefi. Sinyaller belirlenen hedefe ulaştığında başarılı sayılır.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white"
        >
          <option value="all">Tümü</option>
          <option value="tp1">TP1 Gören</option>
          <option value="tp2">TP2 Gören</option>
          <option value="tp3">TP3 Gören</option>
          <option value="stopped">Stop ile Sonuçlanan</option>
        </select>
        
        <select 
          value={symbolFilter} 
          onChange={(e) => setSymbolFilter(e.target.value)}
          className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white"
        >
          <option value="all">Tüm Pariteler</option>
          {symbols.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      
      {/* Signals Table */}
      <Card className="border-slate-700/50 bg-slate-900/30">
        <CardContent className="p-0">
          {/* Table Header */}
          <div className="grid grid-cols-5 gap-4 p-4 border-b border-slate-700/50 text-xs font-semibold text-slate-400 uppercase">
            <div>Parite</div>
            <div>İşlem</div>
            <div>Sonuç</div>
            <div className="text-right">K/Z</div>
            <div className="text-right">Açılış Tarihi</div>
          </div>
          
          {/* Table Body */}
          {filteredSignals.length === 0 ? (
            <div className="p-8 text-center">
              <Clock className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">
                {completedSignals.length === 0 
                  ? 'Henüz tamamlanmış sinyal yok' 
                  : 'Bu filtreye uygun sinyal bulunamadı'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/30">
              {filteredSignals.map((signal, idx) => {
                const outcome = getOutcomeLabel(signal);
                return (
                  <div key={signal.id || idx} className="grid grid-cols-5 gap-4 p-4 items-center hover:bg-slate-800/30 transition-colors">
                    <div className="font-medium text-white">{signal.symbol}</div>
                    <div>
                      <Badge className={signal.action === 'BUY' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>
                        {signal.action === 'BUY' ? 'LONG' : 'SHORT'}
                      </Badge>
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${outcome.bg} ${outcome.color}`}>
                        {outcome.label}
                      </span>
                    </div>
                    <div className={`text-right font-mono font-medium ${(signal.pnl_percent || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {(signal.pnl_percent || 0) >= 0 ? '+' : ''}{(signal.pnl_percent || 0).toFixed(1)}%
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-slate-300">
                        {signal.created_at ? new Date(signal.created_at).toLocaleDateString('tr-TR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : '-'}
                      </div>
                      {signal.closed_at && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          Kapanış: {new Date(signal.closed_at).toLocaleDateString('tr-TR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400">
            <strong className="text-slate-300">Yasal Uyarı:</strong> Bu istatistikler Borsamex algoritmaları tarafından üretilmiştir. 
            Geçmiş performans, gelecekteki sonuçların garantisi değildir. Yatırım kararlarınızı kendi araştırmanıza dayandırın.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HistoryTab;
